/* Lightweight storefront toast — slides up from bottom-right, auto-
   dismisses after 4s. Survives multiple in-flight calls (each gets
   its own DOM node so they stack vertically). Pre-fix the storefront
   had no error feedback at all — overselling rejections from Shopify
   showed a fleeting "Error" on the button and disappeared, leaving
   the customer wondering what happened. */
function stillToast(message, kind) {
  const container = (function () {
    let c = document.getElementById('still-toast-container');
    if (c) return c;
    c = document.createElement('div');
    c.id = 'still-toast-container';
    c.style.cssText = 'position:fixed;bottom:20px;right:20px;z-index:99999;display:flex;flex-direction:column;gap:8px;pointer-events:none;';
    document.body.appendChild(c);
    return c;
  })();
  const toast = document.createElement('div');
  const isError = kind === 'error';
  toast.style.cssText = [
    'background:' + (isError ? '#d04040' : '#222'),
    'color:#fff',
    'padding:12px 16px',
    'border-radius:6px',
    'font-size:13px',
    'font-family:var(--font-still-body, Inter, sans-serif)',
    'box-shadow:0 4px 16px rgba(0,0,0,0.2)',
    'max-width:340px',
    'pointer-events:auto',
    'opacity:0',
    'transform:translateY(8px)',
    'transition:opacity 180ms ease, transform 180ms ease',
  ].join(';');
  toast.textContent = message;
  container.appendChild(toast);
  /* Trigger transition on next frame */
  requestAnimationFrame(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateY(0)';
  });
  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(8px)';
    setTimeout(() => toast.remove(), 200);
  }, 4000);
}

/* Read /cart.js once; return Map<variantId, qtyInCart>. Used by the
   quick-add modal to subtract already-in-cart copies from the stepper
   max so the customer can't stage an oversell that Shopify will
   silently reject downstream. */
async function stillFetchCartQuantities() {
  try {
    const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    if (!r.ok) return new Map();
    const cart = await r.json();
    const m = new Map();
    for (const item of (cart.items || [])) {
      m.set(item.variant_id, (m.get(item.variant_id) || 0) + (item.quantity || 0));
    }
    return m;
  } catch (e) {
    return new Map();
  }
}

async function stillAddToCart(variantId, btn, quantity) {
  /* `quantity` is optional — defaults to 1 so pre-existing call sites
     (collection grid quick-add, etc.) keep working. PDP passes the
     value from #pdp-qty. */
  const qty = Math.max(1, parseInt(quantity, 10) || 1);
  const originalLabel = btn.textContent;
  btn.classList.add('loading');
  btn.textContent = '…';
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty }),
    });
    if (res.ok) {
      btn.textContent = '✓';
      setTimeout(() => {
        /* Restore the button's original label rather than hardcoding
           'Add' — the PDP button reads "Add to cart", quick-add reads
           "Add", etc. */
        btn.textContent = originalLabel;
        btn.classList.remove('loading');
      }, 1200);
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      /* Update Dawn cart icon bubble via section rendering */
      stillUpdateCartBubble();
      return { ok: true };
    } else {
      /* Shopify returns a JSON body with a `description` (and sometimes
         `message`) field on rejection. Common case for a TCG store:
         policy=DENY + cart already at stock cap → 422 with description
         like "Only 2 in stock — already in cart." Surface that as a
         toast so the customer understands why nothing happened. */
      let detail = 'Couldn\'t add to cart.';
      try {
        const err = await res.json();
        detail = err.description || err.message || detail;
      } catch (_) { /* response wasn't JSON; keep the generic */ }
      stillToast(detail, 'error');
      btn.textContent = originalLabel;
      btn.classList.remove('loading');
      return { ok: false, status: res.status, message: detail };
    }
  } catch (e) {
    stillToast('Network error — check your connection.', 'error');
    btn.textContent = originalLabel;
    btn.classList.remove('loading');
    return { ok: false, message: String(e) };
  }
}

async function stillUpdateCartBubble() {
  try {
    const res = await fetch('/?sections=cart-icon-bubble');
    if (!res.ok) return;
    const json = await res.json();
    const html = json['cart-icon-bubble'];
    if (!html) return;
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) {
      /* Section response is <div id="shopify-section-cart-icon-bubble">...content...</div>
         The content inside is what goes into the <a id="cart-icon-bubble"> */
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const sectionDiv = tmp.querySelector('.shopify-section') || tmp.firstElementChild;
      if (sectionDiv) {
        bubble.innerHTML = sectionDiv.innerHTML;
      }
    }
  } catch (e) { /* silent */ }
}
